/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React from 'react';
import { Card, Button, ProgressBar, Row, Col, Image } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

export interface IProgressItem {
  icon?: string | any;
  title?: string;
  color?: string;
  percentage?: number;
}

export interface IProgressTrackWidgetProps {
  title?: string;
  buttonTitle?: string;
  buttonLink?: string;
  progressItems?: IProgressItem[];
}

const isValidUrl = (urlString) => {
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

export const ProgressTrackWidget = ({
  title,
  buttonTitle,
  buttonLink,
  progressItems,
}: IProgressTrackWidgetProps) => {
  const navigate = useNavigate();
  const _getIcon = (Icon: any) => {
    let _icon: any = [];
    if (typeof Icon === 'string') {
      if (isValidUrl(Icon)) {
        _icon.push(
          <Image
            key={`image-icon`}
            src={Icon}
            width={60}
            height={60}
            className="icon icon-sm text-gray-500"
          />
        );
      } else {
        const HeroIcon = require('@heroicons/react/solid');
        const NavIcon = HeroIcon[Icon];
        if (NavIcon) {
          _icon.push(
            <NavIcon
              key={`hero-icon`}
              src={Icon}
              className="icon icon-sm text-gray-500"
            />
          );
        }
      }
      return _icon;
    }
    if (Icon) {
      _icon.push(
        <Icon key={`hero-icon-2`} className="icon icon-sm text-gray-500" />
      );
    }
    return _icon;
  };

  const _renderButtonIf = () => {
    if (buttonTitle) {
      return (
        <Button
          variant="primary"
          size="sm"
          onClick={() => buttonLink && navigate(buttonLink)}
        >
          {buttonTitle}
        </Button>
      );
    }
  };

  const _renderProgress = (pi: IProgressItem, index: number) => {
    const rowClassName = index === progressItems?.length - 1 ? 'mb-3' : 'mb-4';
    return (
      <Row
        key={`progress-item-${index}`}
        className={`d-flex align-items-center ${rowClassName}`}
      >
        <Col xs="auto">{_getIcon(pi?.icon)}</Col>
        <Col>
          <div className="progress-wrapper">
            <div className="progress-info">
              <h6 className="mb-0">{pi?.title}</h6>
              <small className="fw-bold text-gray-500">
                <span>{pi?.percentage} %</span>
              </small>
            </div>
            <ProgressBar
              variant={pi?.color}
              now={pi?.percentage}
              min={0}
              max={100}
              className="mb-0"
            />
          </div>
        </Col>
      </Row>
    );
  };

  const _renderProgressItemIf = () => {
    if (progressItems) {
      return (
        <Card.Body>
          {progressItems?.map((pi, index) => _renderProgress(pi, index))}
        </Card.Body>
      );
    }
  };

  if (title) {
    return (
      <>
        <Card border="0" className="shadow">
          <Card.Header className="border-bottom d-flex align-items-center justify-content-between">
            <h2 className="fs-5 fw-bold mb-0">{title}</h2>
            {_renderButtonIf()}
          </Card.Header>
          {_renderProgressItemIf()}
        </Card>
      </>
    );
  }
};

ProgressTrackWidget.defaultProps = {};
